import React from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  CheckCircleOutline as AcceptIcon,
  PersonAddAlt as AssignIcon,
  Close as DismissIcon,
} from '@mui/icons-material';

import type { TranscriptExtraction } from '../../types/models';
import { transcriptsCopy } from '../../copy/transcripts';
import ConfidenceBadge from './ConfidenceBadge';
import WhyAffordance from '../ai/WhyAffordance';

// ---------------------------------------------------------------------------
// ExtractionCard — one row in the extractions tab. Confidence-first, with
// verb-first primary CTAs (Accept into plan / Assign / Dismiss) next to the
// title so the user can act without scanning to the far edge of the screen.
//
// Accepted and Dismissed extractions render with a subdued state and no
// action buttons — the card becomes a record of what happened.
// ---------------------------------------------------------------------------

const TYPE_COLOURS: Record<string, string> = {
  Task: '#6C9BFF',
  Decision: '#FF8A65',
  Briefing: '#69F0AE',
  Review: '#FFB74D',
  FollowUp: '#CE93D8',
  Draft: '#4DD0E1',
  Reading: '#AED581',
};

const PRIORITY_COLOURS: Record<string, string> = {
  Critical: '#FF5252',
  High: '#FF8A65',
  Medium: '#6C9BFF',
  Low: '#69F0AE',
};

interface ExtractionCardProps {
  extraction: TranscriptExtraction;
  selected?: boolean;
  onToggleSelect?: () => void;
  onAccept?: () => void;
  onAssign?: () => void;
  onDismiss?: () => void;
  disabled?: boolean;
}

function SoftChip({
  label,
  colour,
}: {
  label: string;
  colour: string;
}) {
  return (
    <Chip
      label={label}
      size="small"
      sx={{
        bgcolor: `${colour}22`,
        color: colour,
        fontWeight: 600,
        fontSize: '0.72rem',
        height: 22,
      }}
    />
  );
}

export function ExtractionCard({
  extraction,
  selected = false,
  onToggleSelect,
  onAccept,
  onAssign,
  onDismiss,
  disabled = false,
}: ExtractionCardProps) {
  const isHandled = extraction.wasAccepted || extraction.wasDismissed;
  const stateLabel = extraction.wasAccepted
    ? transcriptsCopy.statusAccepted
    : extraction.wasDismissed
    ? transcriptsCopy.statusDismissed
    : null;

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        borderRadius: 2,
        borderColor: selected ? 'primary.main' : 'divider',
        bgcolor: selected ? 'rgba(108, 155, 255, 0.04)' : 'background.paper',
        opacity: isHandled ? 0.6 : 1,
        transition: 'border-color 120ms, background-color 120ms, opacity 120ms',
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        {/* Selection checkbox (only while actionable) */}
        {!isHandled && onToggleSelect && (
          <Checkbox
            size="small"
            checked={selected}
            onChange={onToggleSelect}
            sx={{ mt: -0.5, p: 0.5 }}
          />
        )}

        {/* Main content */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            alignItems={{ md: 'flex-start' }}
            justifyContent="space-between"
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {extraction.title}
                </Typography>
                <WhyAffordance
                  rationale={extraction.rationale}
                  size="compact"
                  label="Why this extraction"
                />
                <ConfidenceBadge score={extraction.confidenceScore} />
                {stateLabel && (
                  <Chip
                    label={stateLabel}
                    size="small"
                    sx={{
                      bgcolor: extraction.wasAccepted
                        ? 'rgba(105, 240, 174, 0.15)'
                        : 'rgba(136, 146, 168, 0.15)',
                      color: extraction.wasAccepted ? '#69F0AE' : '#8892A8',
                      fontSize: '0.65rem',
                      height: 18,
                      fontWeight: 600,
                    }}
                  />
                )}
              </Stack>
              {extraction.description && (
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', mt: 0.5, whiteSpace: 'pre-wrap' }}
                >
                  {extraction.description}
                </Typography>
              )}

              {/* Meta row: type, priority, duration, assignee, due context */}
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
                useFlexGap
                sx={{ mt: 1 }}
              >
                <SoftChip
                  label={extraction.workItemType}
                  colour={TYPE_COLOURS[extraction.workItemType] || '#8892A8'}
                />
                <SoftChip
                  label={extraction.priority}
                  colour={PRIORITY_COLOURS[extraction.priority] || '#8892A8'}
                />
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {extraction.suggestedDurationMinutes} min
                </Typography>
                {extraction.assignedTo && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    for {extraction.assignedTo}
                  </Typography>
                )}
                {extraction.dueContext && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {extraction.dueContext}
                  </Typography>
                )}
              </Stack>
            </Box>

            {/* Per-card actions — only while the extraction is still actionable */}
            {!isHandled && (
              <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                {onAccept && (
                  <Button
                    size="small"
                    variant="contained"
                    color="success"
                    startIcon={<AcceptIcon />}
                    onClick={onAccept}
                    disabled={disabled}
                  >
                    {transcriptsCopy.actionAccept}
                  </Button>
                )}
                {onAssign && (
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<AssignIcon />}
                    onClick={onAssign}
                    disabled={disabled}
                  >
                    {transcriptsCopy.actionAssign}
                  </Button>
                )}
                {onDismiss && (
                  <Button
                    size="small"
                    variant="text"
                    color="inherit"
                    startIcon={<DismissIcon />}
                    onClick={onDismiss}
                    disabled={disabled}
                    sx={{ color: 'text.secondary' }}
                  >
                    {transcriptsCopy.actionDismiss}
                  </Button>
                )}
              </Stack>
            )}
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}

export default ExtractionCard;
